import * as _ from 'lodash';
import * as moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { IWatrReimbursement, IListItem, IWatrRequest } from '@nkr/models/index';
import { Button, Glyphicon, Alert, Panel, Label } from 'react-bootstrap';
import { ReimbursementType, WageAndTravelStatus, WageAndTravelType } from '@nkr/enums/index';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import ReimbursementEntry from './reimbursement-entry';
import { formatMoneyDisplay } from './common/money-input';
import './common/week-by-week-breakdown.css';
import { DonorShieldContext } from './context';
import { openWatrReceiptInSidebar } from '../utils/open-watr-receipt-in-sidebar';
import { donorHasComplicationsEvidence, getActiveComplicationsReportFile } from '../utils/watr-complications-helpers';
import { readAdvancedProgressForBuckets, isIntlArVisitReimbursementEntry } from './advanced-reimbursement-status-buckets';
import {
    findTravelsForReimbursement,
    findWeeksForReimbursement,
    resolveApproverDisplayForReimbursementLog,
    resolveReimbursedDisplayForReimbursementLog
} from './utils/approver-display.utils';

interface IProps {
    isNkrSuper: boolean,
    request: IWatrRequest,
    reimbursements: IWatrReimbursement[],
    types: IListItem[],
    container: any,
    isMobile: boolean,
    usePanel: boolean,
    isReimburseTravel: boolean,
    isReimburseWage: boolean,
    onSaveReimbursementEntry: (type: number, amount: number, confirmationCode: string, date: string, closeAdvancedReimbursementCard?: boolean) => void,
    onReimbursementUpdate: (requestId: number, reimbId: number, type: number, amount: number, confirmationCode: string, date: string) => void,
    openAddReimbursementOnMount?: boolean,
    openReconcileReimbursementEntry?: boolean,
    openIntlArVisitReimbursementEntry?: boolean
}

const isAchTravelOnlyType = (t: number | undefined) =>
    t === WageAndTravelType.ReimburseTravelOnly || t === ReimbursementType.TravelAndLodging;

const WAGE_REIMBURSEMENT_TYPE_IDS = [
    ReimbursementType.Wages,
    ReimbursementType.WagesForComplication,
    ReimbursementType.CreditReturnLostWageReimbursement
];
const EXPENSE_REIMBURSEMENT_TYPE_IDS = [
    ReimbursementType.TravelAndLodging,
    ReimbursementType.TravelAndLodgingForComplication,
    ReimbursementType.DependentCare,
    ReimbursementType.DppReimbursement,
    ReimbursementType.InternationalWorkup
];

const typesForPaymentWorkflow = (types: IListItem[], isTravel: boolean, isWage: boolean) => {
    if (!types || types.length === 0) {
        return types;
    }

    if (isTravel && !isWage) {
        const filtered = types.filter(item => EXPENSE_REIMBURSEMENT_TYPE_IDS.indexOf(item.id) >= 0);

        return filtered.length > 0 ? filtered : types;
    }

    if (isWage && !isTravel) {
        const filtered = types.filter(item => WAGE_REIMBURSEMENT_TYPE_IDS.indexOf(item.id) >= 0);

        return filtered.length > 0 ? filtered : types;
    }

    return types;
};

/** Credit Return rows are offsets — show/sum as negative even if stored absolute. */
const signedReimbursementAmount = (reimbursementType: number | undefined, amount: number | null | undefined): number => {
    const n = Number(amount ?? 0);
    if (reimbursementType === ReimbursementType.CreditReturnLostWageReimbursement) {
        return -Math.abs(n);
    }
    return n;
};

const renderAuditPersonCell = (display: { name: string; dateText: string }) => {
    if (!display.name && !display.dateText) {
        return <span className='watr-week-breakdown-approver-date'>—</span>;
    }

    return (
        <div className='watr-week-breakdown-approver'>
            {display.name ? (
                <div className='watr-week-breakdown-approver-name'>{display.name}</div>
            ) : null}
            {display.dateText && (
                <div className='watr-week-breakdown-approver-date'>{display.dateText}</div>
            )}
        </div>
    );
};

const ReimbursementLog = (props: IProps) => {
    const donorShieldContext = useContext(DonorShieldContext);
    const [addEdit, setAddEdit] = useState<{ show: boolean, data: IWatrReimbursement }>({ show: false, data: null });

    useEffect(() => {
        if (
            props.openReconcileReimbursementEntry
            || props.openIntlArVisitReimbursementEntry
            || props.openAddReimbursementOnMount
        ) {
            setAddEdit({ show: true, data: null });
        }
    }, [
        props.openReconcileReimbursementEntry,
        props.openIntlArVisitReimbursementEntry,
        props.openAddReimbursementOnMount,
        props.request?.id
    ]);
    const [deleteReimbursement, setDeleteReimbursement] = useState<{ show: boolean, data: IWatrReimbursement }>({ show: false, data: null });
    const [expand, setExpand] = useState(false);

    const activeComplicationsReportFile = useMemo(
        () => getActiveComplicationsReportFile(props.request),
        [props.request?.fileUploadRecords]
    );

    const isAdvancedReimbursementRequest = useMemo(() => {
        if (props.openReconcileReimbursementEntry) {
            return true;
        }
        if (props.request?.status === WageAndTravelStatus.PendingAdvancedReimbursement) {
            return true;
        }
        if (readAdvancedProgressForBuckets(props.request)) {
            return true;
        }
        return (props.reimbursements ?? []).some(
            r => r?.reimbursementType === ReimbursementType.AdvancedReimbursement
        );
    }, [props.openReconcileReimbursementEntry, props.request, props.reimbursements]);

    const showComplicationsBanner = useMemo(
        () => donorHasComplicationsEvidence(props.request, props.reimbursements),
        [props.request, props.reimbursements]
    );

    const wagePending = useMemo(() => {
        const req = props.request;
        if (!props.isReimburseWage) {
            return {
                chaseWage: false,
                approvedWeekNumbers: [] as number[],
                approvedWeeksAwaitingAch: 0,
                lwrWorkflow: false,
                chaseAmount: null as number | null,
                chaseSendOn: null as string | null,
                chaseRef: null as string | null,
                anyWagePending: false
            };
        }

        const weekNumbersAwaiting = (req.requestWeeks || [])
            .filter(w =>
                w.status === WageAndTravelReimbursementRequestStatus.AutoApproved
                || w.status === WageAndTravelReimbursementRequestStatus.CenterApproved
                || w.status === WageAndTravelReimbursementRequestStatus.AdminApproved
            )
            .map(w => w.weekRequested)
            .sort((a, b) => a - b);

        const approvedWeeksAwaitingAch = weekNumbersAwaiting.length;

        const chaseWage = !!(req.pendingTransactionConfirmation && !isAchTravelOnlyType(req.achReimbursementType));

        const amt = req.pendingTransactionAmount ?? req.transactionAmount;
        const chaseAmount = typeof amt === 'number' && !isNaN(amt) ? amt : null;
        const sendRaw = req.pendingTransactionSendOn;
        const chaseSendOn = sendRaw
            ? (moment(sendRaw).isValid() ? moment(sendRaw).format('MM/DD/YYYY') : String(sendRaw))
            : null;
        const chaseRef = req.pendingTransactionConfirmation || null;

        const lwrWorkflow = !!req.pendingLWRPayment && !chaseWage && approvedWeeksAwaitingAch === 0;

        const anyWagePending = chaseWage || approvedWeeksAwaitingAch > 0 || lwrWorkflow;

        return {
            chaseWage,
            approvedWeekNumbers: weekNumbersAwaiting,
            approvedWeeksAwaitingAch,
            lwrWorkflow,
            chaseAmount,
            chaseSendOn,
            chaseRef,
            anyWagePending
        };
    }, [props.request, props.isReimburseWage]);

    const derPending = useMemo(() => {
        const req = props.request;
        const empty = {
            chaseTravel: false,
            approvedTravelCount: 0,
            derWorkflow: false,
            chaseAmount: null as number | null,
            chaseSendOn: null as string | null,
            anyDerPending: false
        };

        if (!req || !props.isReimburseTravel) {
            return empty;
        }

        const approvedTravelCount = (req.requestTravels || []).filter(t =>
            t.status === WageAndTravelReimbursementRequestStatus.AutoApproved
            || t.status === WageAndTravelReimbursementRequestStatus.CenterApproved
            || t.status === WageAndTravelReimbursementRequestStatus.AdminApproved
        ).length;

        const chaseTravel = !!(req.pendingTransactionConfirmation && isAchTravelOnlyType(req.achReimbursementType));
        const amt = req.pendingTransactionAmount ?? req.transactionAmount;
        const chaseAmount = typeof amt === 'number' && !isNaN(amt) ? amt : null;
        const sendRaw = req.pendingTransactionSendOn;
        const chaseSendOn = sendRaw
            ? (moment(sendRaw).isValid() ? moment(sendRaw).format('MM/DD/YYYY') : String(sendRaw))
            : null;
        const derWorkflow = !!req.pendingDERPayment && !chaseTravel && approvedTravelCount === 0;
        const anyDerPending = chaseTravel || approvedTravelCount > 0 || derWorkflow;

        return {
            chaseTravel,
            approvedTravelCount,
            derWorkflow,
            chaseAmount,
            chaseSendOn,
            anyDerPending
        };
    }, [props.request, props.isReimburseTravel]);

    const defaultPendingReimbursementType = useMemo(() => {
        if (props.isReimburseTravel && !props.isReimburseWage) {
            return ReimbursementType.TravelAndLodging;
        }

        if (props.isReimburseWage && !props.isReimburseTravel) {
            return ReimbursementType.Wages;
        }

        if (props.request?.achReimbursementType === WageAndTravelType.ReimburseTravelOnly) {
            return ReimbursementType.TravelAndLodging;
        }

        if (props.request?.achReimbursementType === WageAndTravelType.ReimburseWagesOnly) {
            return ReimbursementType.Wages;
        }

        const pendingTravelCount = (props.request?.requestTravels || [])
            .filter(x =>
                x.status >= WageAndTravelReimbursementRequestStatus.AutoApproved
                && x.status <= WageAndTravelReimbursementRequestStatus.AdminApproved
            ).length;
        const pendingWageWeekCount = (props.request?.requestWeeks || [])
            .filter(x =>
                x.status >= WageAndTravelReimbursementRequestStatus.AutoApproved
                && x.status <= WageAndTravelReimbursementRequestStatus.AdminApproved
            ).length;

        if (pendingTravelCount > 0 && pendingWageWeekCount === 0) {
            return ReimbursementType.TravelAndLodging;
        }

        if (pendingWageWeekCount > 0 && pendingTravelCount === 0) {
            return ReimbursementType.Wages;
        }

        return 0;
    }, [props.request?.achReimbursementType, props.request?.requestTravels, props.request?.requestWeeks, props.isReimburseTravel, props.isReimburseWage]);

    const handleShowAddEdit = (data: IWatrReimbursement) => {
        setAddEdit({
            show: true,
            data
        });
    };

    const handleCancelAdd = () => {
        setAddEdit({
            show: false,
            data: null
        });
    };

    const handleReimbursementEntry = (type: number, amount: number, confirmationCode: string, date: string, closeAdvancedReimbursementCard?: boolean) => {
        props.onSaveReimbursementEntry(type, amount, confirmationCode, date, closeAdvancedReimbursementCard);

        setAddEdit({
            show: false,
            data: null
        });
    };

    const handleReimbursementUpdate = (reimbId: number, type: number, amount: number, confirmationCode: string, date: string) => {
        props.onReimbursementUpdate(props.request.id, reimbId, type, amount, confirmationCode, date);

        setAddEdit({
            show: false,
            data: null
        });
    };

    const renderContents = () => {
        const reimbursementsSafe = (props.reimbursements ?? []).filter(
            (data): data is IWatrReimbursement => data != null
        );
        let reimbursementList = _.sortBy(reimbursementsSafe, data => new Date(data.date));
        let total = _.sumBy(reimbursementList, x => signedReimbursementAmount(x.reimbursementType, x.amount));

        let hasDonorId = (props.request.donorId > 0 || props.request.donorShieldId > 0);
        let hasDonatedandConfirmed = moment(props.request.donationDate).isSameOrBefore(moment()) && props.request.donationConfirmed;
        const requiresConfirmedDonation = !isAdvancedReimbursementRequest;
        let showAddReimbursement = hasDonorId && props.isNkrSuper
            && (!requiresConfirmedDonation || hasDonatedandConfirmed);
        const isReimbursementStep = hasDonatedandConfirmed
            && props.request.status > WageAndTravelStatus.PendingNkrVerifyAmount
            && props.request.status < WageAndTravelStatus.Denied;
        const wageEligible = isReimbursementStep && wagePending.anyWagePending;
        const derEligible = isReimbursementStep && derPending.anyDerPending;
        let showWagePendingNotice = false;
        let showDerPendingNotice = false;

        if (props.isReimburseWage && !props.isReimburseTravel) {
            showWagePendingNotice = wageEligible;
        } else if (props.isReimburseTravel && !props.isReimburseWage) {
            showDerPendingNotice = derEligible;
        } else if (props.request?.achReimbursementType === WageAndTravelType.ReimburseTravelOnly) {
            showDerPendingNotice = derEligible;
        } else {
            showWagePendingNotice = wageEligible;

            if (!showWagePendingNotice) {
                showDerPendingNotice = derEligible;
            }
        }

        const showPendingNotice = showWagePendingNotice || showDerPendingNotice;
        const pendingTitle = 'ACH confirmation needed';
        const pendingNote = showDerPendingNotice
            ? 'Approved expense claims still need a payment recorded.'
            : 'Approved wage weeks still need a payment recorded.';

        const advancedProgress = readAdvancedProgressForBuckets(props.request);
        const preOpScheduledDate = (advancedProgress?.preOpScheduledDate || '').trim();
        const hasPreOpScheduled = Number(advancedProgress?.networkRemoteStatus) === 25
            || preOpScheduledDate !== '';
        const intlArVisitEntry = isIntlArVisitReimbursementEntry(props.request)
            && !props.openReconcileReimbursementEntry;
        const reimbursementEntryMode = props.openReconcileReimbursementEntry
            ? 'reconcileAdvanced'
            : intlArVisitEntry
                ? 'intlArVisit'
                : 'standard';
        const reconcileIntro = props.openReconcileReimbursementEntry ? (
            <>
                <Alert bsStyle='info' style={{ marginBottom: 12 }}>
                    <strong style={{ fontSize: 14 }}>Reconcile transactions</strong>
                    <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.45 }}>
                        Enter the <strong>Advanced Reimbursement</strong> amount and confirmation to match your bank or card reconciliation,
                        then choose below whether to close the account and move this request to <strong>Closed</strong>, or save only and keep it on the <strong>Active Card</strong> list.
                    </p>
                </Alert>
                {hasPreOpScheduled && (
                    <Alert bsStyle='warning' style={{ marginBottom: 12 }}>
                        <strong style={{ fontSize: 14 }}>Pre-op also scheduled</strong>
                        <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.45 }}>
                            Pre-op has been scheduled
                            {preOpScheduledDate ? ` for ${preOpScheduledDate}` : ''}.
                            {' '}If workup funding has not been fully reconciled yet, finish that reconciliation and account for whether a pre-op card reload or additional funding is needed before closing the card.
                        </p>
                    </Alert>
                )}
            </>
        ) : intlArVisitEntry ? (
            <>
                <Alert bsStyle='info' style={{ marginBottom: 12 }}>
                    <strong style={{ fontSize: 14 }}>International workup reimbursement (wire / ACH)</strong>
                    <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.45 }}>
                        Amount is prefilled from approved expenses. Type is <strong>International Workup</strong>
                        {props.request.arInternationalWorkupVisitCap != null && props.request.derProgramCap != null && (
                            <>
                                {' '}(visit cap <strong>${props.request.arInternationalWorkupVisitCap.toLocaleString('en-US')}</strong>
                                ; program total <strong>${props.request.derProgramCap.toLocaleString('en-US')}</strong>).
                            </>
                        )}
                    </p>
                </Alert>
            </>
        ) : null;

        return (
            <>
                {reconcileIntro}
                        {showPendingNotice && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    marginBottom: 12,
                                    padding: '12px 14px',
                                    borderRadius: 4,
                                    border: '1px solid #dee2e6',
                                    backgroundColor: '#fff8e6'
                                }}
                            >
                                <div style={{ flex: '1 1 240px', minWidth: 0, fontSize: 14, lineHeight: 1.45 }}>
                                    <>
                                        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                            <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1d23' }}>{pendingTitle}</span>
                                            <Label bsStyle='warning' style={{ fontSize: 11, fontWeight: 600 }}>Pending</Label>
                                        </div>
                                        <div style={{ color: '#4b5563', marginBottom: 8, fontSize: 13 }}>
                                            {pendingNote}
                                        </div>
                                        {showWagePendingNotice && wagePending.approvedWeeksAwaitingAch > 0 && (
                                            <div style={{ color: '#333', marginBottom: 8 }}>
                                                {wagePending.approvedWeeksAwaitingAch} approved week
                                                {wagePending.approvedWeeksAwaitingAch !== 1 ? 's' : ''} (
                                                {wagePending.approvedWeekNumbers.join(', ')}) {' '}
                                                {wagePending.approvedWeeksAwaitingAch !== 1 ? 'are' : 'is'} waiting for ACH payment to post.
                                            </div>
                                        )}
                                        {showDerPendingNotice && derPending.approvedTravelCount > 0 && (
                                            <div style={{ color: '#333', marginBottom: 8 }}>
                                                {derPending.approvedTravelCount} approved expense claim
                                                {derPending.approvedTravelCount !== 1 ? 's' : ''} {' '}
                                                {derPending.approvedTravelCount !== 1 ? 'are' : 'is'} waiting for ACH payment to post.
                                            </div>
                                        )}
                                        {wagePending.chaseWage && (
                                            <div style={{ color: '#333', marginBottom: wagePending.lwrWorkflow || showDerPendingNotice ? 8 : 0 }}>
                                                <div style={{ marginBottom: 4 }}>Payment file match (Chase)</div>
                                                {wagePending.chaseAmount != null && (
                                                    <span>
                                                        {wagePending.chaseAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                                        {wagePending.chaseSendOn && (
                                                            <span>{' · '}Send date {wagePending.chaseSendOn}</span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {showDerPendingNotice && derPending.chaseTravel && !wagePending.chaseWage && (
                                            <div style={{ color: '#333', marginBottom: derPending.derWorkflow ? 8 : 0 }}>
                                                <div style={{ marginBottom: 4 }}>Payment file match (Chase)</div>
                                                {derPending.chaseAmount != null && (
                                                    <span>
                                                        {derPending.chaseAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                                        {derPending.chaseSendOn && (
                                                            <span>{' · '}Send date {derPending.chaseSendOn}</span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {wagePending.lwrWorkflow && (
                                            <div style={{ fontSize: 13, color: '#444', marginBottom: 4 }}>
                                                This request is still flagged for lost-wage payment in the workflow (no approved week rows were returned here—refresh if that looks wrong).
                                            </div>
                                        )}
                                        {showDerPendingNotice && derPending.derWorkflow && (
                                            <div style={{ fontSize: 13, color: '#444', marginBottom: 4 }}>
                                                This request is still flagged for expense payment in the workflow (no approved expense rows were returned here—refresh if that looks wrong).
                                            </div>
                                        )}
                                    </>
                                </div>
                                {showAddReimbursement && !addEdit.show && (
                                    <div style={{ flex: '0 0 auto', alignSelf: 'center' }}>
                                        <Button
                                            bsStyle='primary'
                                            bsSize='sm'
                                            onClick={() => handleShowAddEdit(null)}
                                            style={{ fontWeight: 600, minWidth: 168, padding: '8px 16px' }}
                                        >
                                            <Glyphicon glyph='plus' style={{ marginRight: 6 }} />
                                            Add Reimbursement
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {!addEdit.show && !showPendingNotice && !props.isReimburseWage && showAddReimbursement && (
                            <div style={{ textAlign: 'right', marginBottom: 10 }}>
                                <Button
                                    bsStyle='primary'
                                    bsSize='sm'
                                    onClick={() => handleShowAddEdit(null)}
                                    style={{ fontWeight: 600, padding: '8px 16px' }}
                                >
                                    <Glyphicon glyph='plus' style={{ marginRight: 6 }} />
                                    Add Reimbursement
                                </Button>
                            </div>
                        )}
                        {!addEdit.show && props.isReimburseWage && !showPendingNotice && showAddReimbursement && (
                            <div style={{ textAlign: 'right', marginBottom: 10 }}>
                                <Button
                                    bsStyle='primary'
                                    bsSize='sm'
                                    onClick={() => handleShowAddEdit(null)}
                                    style={{ fontWeight: 600, padding: '8px 16px' }}
                                >
                                    <Glyphicon glyph='plus' style={{ marginRight: 6 }} />
                                    Add Reimbursement
                                </Button>
                            </div>
                        )}

                        {addEdit.show && (
                            <div style={{ marginBottom: 12 }}>
                                <ReimbursementEntry
                                    key={`reimb-entry-${props.request?.id}-${reimbursementEntryMode}-${addEdit.data?.id || 'new'}`}
                                    data={addEdit.data}
                                    isSuperUser={props.isNkrSuper}
                                    request={props.request}
                                    reimbursements={props.reimbursements}
                                    typeList={typesForPaymentWorkflow(props.types, props.isReimburseTravel, props.isReimburseWage)}
                                    isReimburseTravel={props.isReimburseTravel}
                                    isReimburseWage={props.isReimburseWage}
                                    defaultType={
                                        props.openReconcileReimbursementEntry
                                            ? ReimbursementType.AdvancedReimbursement
                                            : intlArVisitEntry
                                                ? ReimbursementType.InternationalWorkup
                                                : defaultPendingReimbursementType
                                    }
                                    entryMode={reimbursementEntryMode}
                                    onNewEntry={handleReimbursementEntry}
                                    onUpdate={handleReimbursementUpdate}
                                    onCancel={handleCancelAdd}
                                />
                            </div>
                        )}

                        {requiresConfirmedDonation && !hasDonatedandConfirmed && (
                            <Alert bsStyle='danger' bsSize='sm'>The donor has not yet donated or their donation has not been confirmed.</Alert>
                        )}

                        {!hasDonorId && (
                            <Alert bsStyle='danger' bsSize='sm'>This request does not have a donor bound to it.  Please contact Ops.</Alert>
                        )}

                        {props.isReimburseWage && showComplicationsBanner && hasDonorId && hasDonatedandConfirmed && (
                            <Alert bsStyle='info' bsSize='sm' style={{ marginBottom: 10 }}>
                                <strong>Complications:</strong>{' '}
                                Yes — this donor has complication-related reimbursement activity on record.
                                {donorShieldContext && activeComplicationsReportFile ? (
                                    <>
                                        {' '}
                                        <Button
                                            bsStyle='link'
                                            bsSize='xsmall'
                                            style={{ padding: 0, verticalAlign: 'baseline' }}
                                            onClick={() =>
                                                openWatrReceiptInSidebar(
                                                    donorShieldContext.downloadFile,
                                                    donorShieldContext.onShowDataSideBar,
                                                    activeComplicationsReportFile.id
                                                )
                                            }
                                        >
                                            View complications report
                                        </Button>
                                    </>
                                ) : props.request.hasActiveComplicationsReport ? (
                                    <span style={{ color: '#555' }}>
                                        {' '}
                                        A complications report is flagged on file, but the file list was not loaded. Refresh this page or open <strong>Uploaded documents</strong> in the accordion above.
                                    </span>
                                ) : (
                                    <span style={{ color: '#555' }}>
                                        {' '}
                                        There is no active complications report PDF on file.
                                    </span>
                                )}
                            </Alert>
                        )}

                        <div className='watr-week-breakdown' style={{ marginTop: 0 }}>
                            <div className='watr-week-breakdown-head'>Payment history</div>
                            {(reimbursementList == null || reimbursementList.length == 0) ? (
                                <div className='watr-week-breakdown-formula'>No payments have been recorded yet.</div>
                            ) : (
                                <>
                                    <div className='watr-week-breakdown-scroll'>
                                        <table className='watr-week-breakdown-table'>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Type</th>
                                                    <th>Request</th>
                                                    <th className='amt'>Amount</th>
                                                    <th>Confirmation</th>
                                                    {props.isNkrSuper && (
                                                        <>
                                                            <th className='amt'>Approved</th>
                                                            <th className='amt'>Reimbursed</th>
                                                            <th className='amt'>Edit</th>
                                                        </>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reimbursementList.map((item, index) => {
                                                    const type = props.types.find(x => x.id == item.reimbursementType);
                                                    const typeDescription = (type?.description
                                                        || (item.reimbursementType === ReimbursementType.InternationalWorkup
                                                            ? 'International Workup'
                                                            : `Unknown (${item.reimbursementType ?? 'n/a'})`))
                                                        .replace(/\s+Reimbursement$/i, '');
                                                    let request = '';

                                                    if (item.reimbursementType == 1 && item.weeksRequested !== null && item.weeksRequested != '') {
                                                        request = item.weeksRequested;
                                                    } else if (item.reimbursementType == 2 && item.travelsRequested !== null && item.travelsRequested != '') {
                                                        request = item.travelsRequested;
                                                    }

                                                    let approver: { name: string; dateText: string } | undefined;
                                                    let reimbursed: { name: string; dateText: string } | undefined;

                                                    if (props.isNkrSuper) {
                                                        const linkedWeeks = findWeeksForReimbursement(
                                                            props.request?.requestWeeks,
                                                            item.id,
                                                            item.weeksRequested
                                                        );
                                                        const linkedTravels = findTravelsForReimbursement(
                                                            props.request?.requestTravels,
                                                            item.id
                                                        );

                                                        approver = resolveApproverDisplayForReimbursementLog(
                                                            linkedWeeks,
                                                            linkedTravels,
                                                            true
                                                        );
                                                        reimbursed = resolveReimbursedDisplayForReimbursementLog(
                                                            item,
                                                            true
                                                        );
                                                    }

                                                    const amount = signedReimbursementAmount(item.reimbursementType, item.amount);

                                                    return (
                                                        <tr key={item.id || index}>
                                                            <td className='wk'>{index + 1}</td>
                                                            <td title={type?.description || typeDescription}>{typeDescription}</td>
                                                            <td className='wk'>{request || '—'}</td>
                                                            <td className='amt'>{formatMoneyDisplay(amount)}</td>
                                                            <td title={item.confirmationCode || undefined}>{item.confirmationCode || '—'}</td>
                                                            {props.isNkrSuper && (
                                                                <>
                                                                    <td>{renderAuditPersonCell(approver!)}</td>
                                                                    <td>{renderAuditPersonCell(reimbursed!)}</td>
                                                                    <td className='amt'>
                                                                        <Button
                                                                            type='button'
                                                                            bsStyle='default'
                                                                            bsSize='sm'
                                                                            className='watr-week-breakdown-save'
                                                                            title='Edit payment'
                                                                            aria-label='Edit payment'
                                                                            onClick={() => handleShowAddEdit(item)}
                                                                        >
                                                                            <Glyphicon glyph='pencil' />
                                                                        </Button>
                                                                    </td>
                                                                </>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className='watr-week-breakdown-formula is-total'>
                                        <span>Total</span>
                                        <span className='amt'>{formatMoneyDisplay(total)}</span>
                                    </div>
                                </>
                            )}
                        </div>
            </>
        );
    };

    if (props.usePanel) {
        const hasDonatedandConfirmed = moment(props.request.donationDate).isSameOrBefore(moment()) && props.request.donationConfirmed;
        const isReimbursementStep = hasDonatedandConfirmed
            && props.request.status > WageAndTravelStatus.PendingNkrVerifyAmount
            && props.request.status < WageAndTravelStatus.Denied;
        const headingBadge = isReimbursementStep && (wagePending.anyWagePending || derPending.anyDerPending)
            ? <Label bsStyle='warning' style={{ fontSize: 11 }}>Pending</Label>
            : null;

        return (
            <Panel eventKey='4'>
                <Panel.Heading style={{ paddingTop: 8, paddingBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 8 }}>
                        <Panel.Title toggle style={{ fontSize: 15, marginTop: 0, marginBottom: 0, flex: '1 1 auto' }}>
                            Reimbursement Payments
                        </Panel.Title>
                        {headingBadge}
                    </div>
                </Panel.Heading>
                <Panel.Body collapsible>
                    {renderContents()}
                </Panel.Body>
            </Panel>
        );
    }

    return renderContents();
}

export default ReimbursementLog;